// ============================================
// Inventory Hooks - TanStack Query
// ============================================

import { useMemo } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { API_ENDPOINTS } from '@conai/api-client';
import type {
  CreateInventoryAdjustmentRequest,
  InventoryListParams,
  AdjustmentListParams,
  InventoryStock,
  BulkAdjustItem,
  BulkAdjustResponse,
  InventoryDriftParams,
  LossStatsParams,
  CreateBatchRequest,
  BatchListParams,
  ExpiringBatchParams,
  StocktakeListParams,
  PaginatedResponse,
  UUID,
} from '@conai/shared-types';
import { useInventoryService } from '../providers/ApiProvider';
import { queryKeys } from '../config/query-client';
import { toStockStatusParam } from '../utils/stockStatus';

const SELLABLE_PAGE_SIZE = 50;

/**
 * Số dòng xin cho MỖI TRANG khi lấy trọn phạm vi một cửa hàng (useInventoryScope).
 *
 * Cố tình xin nhiều hơn trần 100 của BE: bản BE mới sẽ tự cắt xuống 100 và báo
 * còn trang tiếp trong `next`/`total_pages` (ta đọc meta để lấy nốt), còn bản BE
 * cũ chưa chặn trần thì trả luôn tới 500 dòng trong một lượt như trước. Đặt
 * thẳng 100 sẽ làm bản cũ (chưa có meta phân trang) tụt từ 500 xuống 100 dòng.
 */
const SCOPE_PER_STORE_PAGE_SIZE = 500;

/** Chặn trên số vòng lặp phân trang cho mỗi cửa hàng — tránh vòng lặp vô tận nếu meta sai. */
const SCOPE_MAX_PAGES = 20;

/** Bóc mảng record khỏi mọi hình dạng payload (mảng trần / {results} / {data}). */
function extractRows(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== 'object') return [];

  const envelope = payload as { results?: unknown[]; items?: unknown[]; data?: unknown };
  if (Array.isArray(envelope.results)) return envelope.results;
  if (Array.isArray(envelope.items)) return envelope.items;
  if (Array.isArray(envelope.data)) return envelope.data;
  return [];
}

/**
 * Còn trang sau không? CHỈ tin vào metadata tường minh của BE
 * (`next` / `total_pages`, ở gốc hoặc trong `meta`).
 *
 * Cố ý KHÔNG đoán bằng "số dòng trả về == page_size đã xin": endpoint nào lờ
 * param `page` đi sẽ trả lại y nguyên trang đầu và ta lặp mù cho tới trần.
 */
function hasMorePages(payload: unknown, currentPage: number): boolean {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return false;

  const root = payload as Record<string, unknown>;
  const meta = (root.meta && typeof root.meta === 'object' ? root.meta : root) as Record<string, unknown>;

  const next = meta.next ?? root.next;
  if (typeof next === 'string' && next.trim().length > 0) return true;
  if (next === true) return true;

  const totalPages = Number(meta.total_pages ?? root.total_pages);
  return Number.isFinite(totalPages) && totalPages > currentPage;
}

/**
 * CHỐT CHẶN DUY NHẤT trước khi `stock_status` ra khỏi FE.
 *
 * Từ khi BE trả **400** cho giá trị lạ (thay vì trả rỗng), một nhãn đã dịch hay
 * một giá trị dropdown chưa chuẩn hoá lọt xuống đây là hỏng nguyên bảng. Vì thế
 * mọi request tới sellable-products đều đi qua hàm này: giá trị nào không quy
 * được về đúng `IN_STOCK`/`LOW_STOCK`/`OUT_OF_STOCK` thì param bị **xoá khỏi
 * query**, chứ không gửi chuỗi rỗng (ApiClient chỉ lọc `undefined`, `''` vẫn
 * được gửi và cũng đủ để ăn 400).
 */
function withSafeStockStatus(params?: InventoryListParams): InventoryListParams | undefined {
  if (!params) return params;

  /**
   * `Omit` trước rồi mới nới thành `unknown`: nếu giao thẳng
   * `InventoryListParams & { stock_status?: unknown }` thì `StockStatus & unknown`
   * rút gọn lại về `StockStatus`, TS coi phép so với `''` là thừa và báo TS2367 —
   * đúng cái mà chốt chặn này sinh ra để bắt (giá trị runtime lọt ngoài kiểu).
   */
  const { stock_status: rawStatus, ...rest } = params as Omit<InventoryListParams, 'stock_status'> & {
    stock_status?: unknown;
  };
  if (rawStatus === undefined || rawStatus === null || rawStatus === '') {
    return rest as InventoryListParams;
  }

  const wireStatus = toStockStatusParam(rawStatus);
  return (wireStatus ? { ...rest, stock_status: wireStatus } : rest) as InventoryListParams;
}

async function fetchSellableProductsPage(
  inventoryService: unknown,
  rawParams?: InventoryListParams
): Promise<PaginatedResponse<InventoryStock>> {
  const params = withSafeStockStatus(rawParams);
  const serviceWithMethod = inventoryService as {
    listSellableProducts?: (params?: InventoryListParams) => Promise<PaginatedResponse<InventoryStock>>;
  };

  if (typeof serviceWithMethod.listSellableProducts === 'function') {
    return serviceWithMethod.listSellableProducts(params);
  }

  const serviceWithClient = inventoryService as {
    client?: {
      get: <T>(url: string, query?: Record<string, string | number | boolean | undefined>) => Promise<T>;
    };
  };

  if (typeof serviceWithClient.client?.get === 'function') {
    return serviceWithClient.client.get<PaginatedResponse<InventoryStock>>(
      API_ENDPOINTS.INVENTORY_SELLABLE_PRODUCTS,
      params as Record<string, string | number | boolean | undefined>
    );
  }

  throw new Error('Inventory service cannot call sellable-products endpoint');
}

/**
 * Lấy TRỌN danh sách sellable-products của một phạm vi params (thường là một
 * cửa hàng), đi hết các trang mà BE báo còn.
 *
 * Vì sao cần: BE chặn `page_size` ở 100, trong khi useInventoryScope gộp nhiều
 * cửa hàng rồi phân trang ở client nên phải cầm đủ dòng mới tính đúng tổng ở
 * footer. Một request duy nhất giờ chỉ còn 100 dòng/cửa hàng.
 *
 * Dừng khi: hết trang theo meta, hoặc trang trả về rỗng, hoặc chạm
 * `SCOPE_MAX_PAGES`. Với BE cũ (không có meta phân trang) vòng lặp dừng ngay
 * sau trang đầu — hành vi y như trước khi thêm hàm này.
 */
async function fetchAllSellableProducts(
  inventoryService: unknown,
  params: InventoryListParams
): Promise<PaginatedResponse<InventoryStock>> {
  const merged: unknown[] = [];

  for (let page = 1; page <= SCOPE_MAX_PAGES; page += 1) {
    const payload = await fetchSellableProductsPage(inventoryService, { ...params, page });
    const rows = extractRows(payload);
    merged.push(...rows);

    if (rows.length === 0 || !hasMorePages(payload, page)) break;
  }

  return {
    count: merged.length,
    next: null,
    previous: null,
    results: merged,
  } as unknown as PaginatedResponse<InventoryStock>;
}

// ============================================
// Inventory List Hooks
// ============================================

/**
 * Hook to list inventory stocks with pagination and filters
 */
export function useInventory(
  filters: InventoryListParams = {},
  options?: {
    refetchInterval?: number;
    refetchOnWindowFocus?: boolean;
    staleTime?: number;
    /** Bug #24: queryClient đặt refetchOnMount:false toàn cục — số tồn kho cần
     *  'always' để mỗi lần mở trang là lấy số mới, không hiện lại cache cũ. */
    refetchOnMount?: boolean | 'always';
  }
) {
  const inventoryService = useInventoryService();

  return useQuery({
    queryKey: queryKeys.inventory.list(filters as Record<string, unknown>),
    queryFn: () => inventoryService.list(filters),
    ...options,
  });
}

/**
 * Hook to list inventory by store
 */
export function useInventoryByStore(
  storeId: UUID | undefined,
  filters: Omit<InventoryListParams, 'store'> = {},
  options?: { enabled?: boolean; requireStore?: boolean }
) {
  const inventoryService = useInventoryService();
  const page = filters.page ?? 1;
  const pageSize = filters.page_size ?? SELLABLE_PAGE_SIZE;

  return useQuery({
    queryKey: queryKeys.inventory.list({
      ...filters,
      store: storeId,
      store_id: storeId,
      only_available: false,
      page,
      page_size: pageSize,
    } as Record<string, unknown>),
    queryFn: () => {
      if (storeId) {
        return fetchSellableProductsPage(inventoryService, {
          ...(filters as InventoryListParams),
          store: storeId,
          ...({ store_id: storeId } as unknown as InventoryListParams),
          ...({ only_available: false } as unknown as InventoryListParams),
          page,
          page_size: pageSize,
        } as InventoryListParams);
      }

      // Không có store ("All Locations"): sellable-products bắt buộc store (400),
      // nên rơi về endpoint /inventory/ tổng quát. Endpoint này lọc trạng thái
      // bằng param `status` (tiền lệ: getOutOfStock) thay vì `stock_status`.
      //
      // /inventory/ trả mảng KHÔNG kèm count -> phân trang server bất khả thi
      // (footer kẹt "20 of 20"). Workaround: fetch 1 lần page_size lớn rồi tự
      // phân trang client — count thật = độ dài danh sách đầy đủ.
      //
      // `stock_status` phải quy về đúng 3 giá trị chuẩn TRƯỚC khi đổi tên thành
      // `status`; không nhận diện được thì bỏ hẳn param (không lọc) thay vì gửi
      // giá trị thô.
      const { stock_status, ...rest } = filters as InventoryListParams & { stock_status?: unknown };
      const wireStatus = toStockStatusParam(stock_status);
      return inventoryService
        .list({
          ...(rest as InventoryListParams),
          ...(wireStatus ? ({ status: wireStatus } as unknown as InventoryListParams) : {}),
          // Bug #145: nhánh "All Locations" trước đây không gửi only_available=false
          // (nhánh có store thì có) → BE loại các record hết hàng, tổng ít hơn
          // từng cửa hàng cộng lại.
          ...({ only_available: false } as unknown as InventoryListParams),
          page: 1,
          page_size: 500,
        } as InventoryListParams)
        .then((response) => {
          const raw = response as unknown;
          const rows: unknown[] = Array.isArray(raw)
            ? raw
            : ((raw as { results?: unknown[] })?.results ?? []);
          const start = (page - 1) * pageSize;
          return {
            count: rows.length,
            next: start + pageSize < rows.length ? 'client-side' : null,
            previous: page > 1 ? 'client-side' : null,
            results: rows.slice(start, start + pageSize),
          } as unknown as PaginatedResponse<InventoryStock>;
        });
    },
    // requireStore=false: cho phép query không gắn store (xem tất cả location).
    enabled: options?.enabled !== false && (options?.requireStore === false || !!storeId),
  });
}

/**
 * Tồn kho theo PHẠM VI cửa hàng — một cửa hàng hay "All Locations" đều đi qua đây.
 *
 * Bug #157: trước đây "All Locations" rơi về endpoint `/inventory/` trong khi
 * chọn 1 cửa hàng lại dùng `/inventory/sellable-products/`. Hai endpoint trả tập
 * dữ liệu khác nhau nên tổng "All Locations" lại ÍT hơn từng cửa hàng cộng lại.
 * Nay cả hai trường hợp đều gọi `/inventory/sellable-products/`.
 *
 * Gọi MỘT REQUEST CHO MỖI CỬA HÀNG trong phạm vi rồi gộp, thay vì một request
 * bỏ trống `store_id`. Bản dùng một request đã bị QA đánh Failed lần hai: nó phụ
 * thuộc vào việc BE hiểu "không truyền store_id" đúng bằng "mọi cửa hàng" —
 * đúng cái điều kiện mà bug này nghi ngờ. Fan-out thì "All Locations" bằng tổng
 * các cửa hàng THEO CẤU TRÚC, không thể nhỏ hơn khi xem riêng một cửa hàng, bất
 * kể BE diễn giải tham số thế nào.
 *
 * Đánh đổi: N request với N = số cửa hàng (hiện là vài cái). Chấp nhận được, vì
 * số liệu tồn kho sai thì nguy hiểm hơn nhiều so với thêm vài request.
 *
 * LỌC TRẠNG THÁI đã chuyển xuống SERVER: truyền `filters.stock_status` và BE lọc
 * bằng SQL đúng công thức của utils/stockStatus.ts. Phía gọi KHÔNG lọc lại lần
 * nữa — lọc hai lần là hai công thức phải trùng nhau vĩnh viễn, lệch một chút là
 * bảng thiếu dòng mà không ai biết vì sao. Mỗi giá trị đều đi qua
 * `withSafeStockStatus()` nên không thể gửi giá trị lạ (BE trả 400).
 *
 * Hook trả TOÀN BỘ danh sách đã gộp (đã lọc sẵn ở server); phân trang do phía
 * gọi tự làm vì tập kết quả là hợp của nhiều cửa hàng.
 */
export function useInventoryScope(
  storeIds: UUID[],
  filters: Omit<InventoryListParams, 'store' | 'page' | 'page_size'> = {},
  options?: { enabled?: boolean }
) {
  const inventoryService = useInventoryService();
  const enabled = options?.enabled !== false && storeIds.length > 0;
  const filtersKey = JSON.stringify(filters ?? {});

  const results = useQueries({
    queries: storeIds.map((storeId) => ({
      queryKey: queryKeys.inventory.list({
        ...filters,
        store_id: storeId,
        only_available: false,
        include_inactive: true,
        page_size: SCOPE_PER_STORE_PAGE_SIZE,
      } as Record<string, unknown>),
      queryFn: () =>
        // fetchAll..., không phải fetch...Page: BE chặn page_size ở 100 nên một
        // request không còn đủ để cầm trọn danh sách một cửa hàng.
        fetchAllSellableProducts(inventoryService, {
          ...(filters as InventoryListParams),
          store: storeId,
          ...({ store_id: storeId } as unknown as InventoryListParams),
          ...({ only_available: false } as unknown as InventoryListParams),
          // Trang Inventory quản lý HÀNG TRONG KHO, không phải catalog. Hàng đã
          // nhập về phải thấy kể cả khi sản phẩm còn DRAFT/INACTIVE —
          // sellable-products mặc định chỉ trả sản phẩm ACTIVE nên sẽ giấu mất
          // tồn kho có thật (bug #158).
          ...({ include_inactive: true } as unknown as InventoryListParams),
          page_size: SCOPE_PER_STORE_PAGE_SIZE,
        } as InventoryListParams),
      enabled,
      // Bug #24/#158: queryClient đặt refetchOnMount:false toàn cục, nên sau khi
      // nhập kho xong quay lại trang Inventory vẫn thấy số cũ.
      refetchOnMount: 'always' as const,
      staleTime: 0,
    })),
  });

  const isLoading = enabled && results.some((r) => r.isLoading);
  const isFetching = enabled && results.some((r) => r.isFetching);
  const error = results.find((r) => r.error)?.error ?? null;

  // useQueries trả mảng mới mỗi render — chốt dependency bằng "chữ ký" thay vì
  // chính mảng results, nếu không useMemo chạy lại mỗi lần render.
  //
  // PHẢI có `dataUpdatedAt`. Chữ ký chỉ đếm SỐ DÒNG thì sau khi nhập kho xong,
  // dữ liệu mới về với current_stock 10 -> 30 nhưng số dòng không đổi -> chữ ký
  // không đổi -> useMemo trả lại đúng mảng cũ, bảng vẫn hiện số cũ. Đó chính là
  // triệu chứng mà refetchOnMount:'always' được thêm vào để chữa (bug #24/#158).
  const signature = `${filtersKey}|${storeIds.join(',')}|${results
    .map((r) => `${r.dataUpdatedAt}:${r.data ? extractRows(r.data).length : -1}`)
    .join('|')}`;

  const rows = useMemo(() => {
    const merged: unknown[] = [];
    const seen = new Set<string>();

    for (const result of results) {
      for (const row of extractRows(result.data)) {
        const record = row as Record<string, unknown>;
        // Khoá theo (store, product): một sản phẩm bán ở 2 cửa hàng là 2 DÒNG tồn
        // kho riêng. Gộp theo product là làm mất hàng — đúng triệu chứng bug #157.
        const storeKey = String(record.store ?? record.store_id ?? '');
        const productKey = String(record.product ?? record.product_id ?? '');
        const key = `${storeKey}:${productKey}` !== ':' ? `${storeKey}:${productKey}` : String(record.id ?? '');

        if (key && seen.has(key)) continue;
        if (key) seen.add(key);
        merged.push(row);
      }
    }

    return merged as InventoryStock[];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  return { rows: enabled ? rows : [], isLoading, isFetching, error };
}

/**
 * Hook to get inventory for a specific product in a store
 */
export function useProductInventory(
  productId: UUID | undefined,
  storeId: UUID | undefined,
  options?: { enabled?: boolean }
) {
  const inventoryService = useInventoryService();

  return useQuery({
    queryKey: queryKeys.inventory.detail(productId ?? '', storeId ?? ''),
    queryFn: () => inventoryService.getByProduct(productId!, storeId!),
    enabled: options?.enabled !== false && !!productId && !!storeId,
  });
}

// ============================================
// Inventory Stats Hooks
// ============================================

/**
 * Hook to get low stock items for a store
 */
export function useLowStock(
  storeId: UUID | undefined,
  params?: { page?: number; page_size?: number },
  options?: {
    enabled?: boolean;
    refetchInterval?: number;
    refetchOnWindowFocus?: boolean;
    staleTime?: number;
    refetchOnMount?: boolean | 'always';
  }
) {
  const inventoryService = useInventoryService();
  const { enabled, ...queryOptions } = options ?? {};

  return useQuery({
    queryKey: queryKeys.inventory.lowStock(storeId),
    // storeId optional: khi chưa chọn store thì lấy low-stock của tất cả store
    queryFn: () => inventoryService.getLowStock(storeId, params),
    enabled: enabled !== false,
    ...queryOptions,
  });
}

/**
 * Hook to get out of stock items for a store
 */
export function useOutOfStock(
  storeId: UUID | undefined,
  params?: { page?: number; page_size?: number },
  options?: { enabled?: boolean }
) {
  const inventoryService = useInventoryService();

  return useQuery({
    queryKey: queryKeys.inventory.outOfStock(storeId),
    queryFn: () => inventoryService.getOutOfStock(storeId!, params),
    enabled: options?.enabled !== false && !!storeId,
  });
}

// ============================================
// Inventory Adjustment Hooks
// ============================================

/**
 * Hook to list inventory adjustments
 */
export function useInventoryAdjustments(filters: AdjustmentListParams = {}) {
  const inventoryService = useInventoryService();

  return useQuery({
    queryKey: queryKeys.inventory.adjustments(filters as Record<string, unknown>),
    queryFn: () => inventoryService.listAdjustments(filters),
  });
}

/**
 * Hook to create inventory adjustment
 */
export function useCreateAdjustment() {
  const inventoryService = useInventoryService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateInventoryAdjustmentRequest) => inventoryService.adjust(data),
    onSuccess: () => {
      // Invalidate all inventory queries
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all });
      toast.success('Đã điều chỉnh tồn kho thành công');
    },
  });
}

/** Tối đa 100 dòng mỗi lô — BE trả 400 nếu vượt. */
export const BULK_ADJUST_MAX_ITEMS = 100;

/**
 * Điều chỉnh tồn hàng loạt.
 *
 * `onSuccess` KHÔNG đồng nghĩa với "mọi dòng đã vào": endpoint luôn trả 200
 * nên phải phân loại theo `summary`. Ba nhánh toast dưới đây là cố ý — báo
 * "thành công" khi `failed > 0` là kiểu lỗi tệ nhất ở màn tồn kho, người dùng
 * đóng modal và tin rằng kho đã đúng.
 */
export function useBulkAdjust() {
  const inventoryService = useInventoryService();
  const queryClient = useQueryClient();

  return useMutation<BulkAdjustResponse, unknown, BulkAdjustItem[]>({
    mutationFn: (items) => inventoryService.bulkAdjust(items),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all });

      // BE không phân biệt "ghi mới" với "đã ghi từ lần gửi trước" — dòng trùng
      // nằm luôn trong `successful`. Nên ở đây chỉ có hai con số, không có
      // nhánh "bỏ qua vì trùng".
      const failed = res.summary?.failed ?? res.failed?.length ?? 0;
      const ok = res.summary?.successful ?? res.successful?.length ?? 0;

      if (failed === 0) {
        toast.success(`Đã điều chỉnh ${ok} dòng`);
      } else if (ok === 0) {
        toast.error(`Không dòng nào được ghi — ${failed} dòng lỗi`);
      } else {
        toast.error(`Ghi được ${ok} dòng, ${failed} dòng LỖI`);
      }
    },
  });
}

/**
 * Thống kê hao hụt theo nguyên nhân (mục #220) — GET /inventory/loss-stats/.
 *
 * BE lên QA đợt deploy kế 09/09/2026, cùng đợt với field `loss_reason` trên
 * adjust/bulk-adjust. `retry: false` để trang gọi hook này không tự thử lại
 * 3 lần trước khi lên QA — vô ích khi biết chắc endpoint chưa có.
 */
export function useLossStats(params?: LossStatsParams) {
  const inventoryService = useInventoryService();

  return useQuery({
    queryKey: [...queryKeys.inventory.all, 'loss-stats', params ?? {}] as const,
    queryFn: () => inventoryService.getLossStats(params),
    retry: false,
  });
}

/**
 * Danh sách lệch tồn (số bán vs số trừ kho).
 *
 * Rỗng có thể nghĩa là "không lệch" HOẶC "job nền chưa từng chạy" — màn hình
 * phải nói rõ khả năng thứ hai, xem InventoryDriftPage.
 */
export function useInventoryDrift(params: InventoryDriftParams = {}) {
  const inventoryService = useInventoryService();

  return useQuery({
    queryKey: queryKeys.inventory.drift(params as Record<string, unknown>),
    queryFn: () => inventoryService.listDrift(params),
  });
}

/**
 * Kiểm đếm: đặt tồn về đúng số đếm được (`SET_ABSOLUTE`).
 *
 * Tham số là SỐ ĐẾM THỰC TẾ chứ không phải mức chênh — server tự tính chênh.
 * Đổi tên từ `useCorrectStock` để không ai truyền nhầm mức chênh vào.
 */
export function useSetStockCount() {
  const inventoryService = useInventoryService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      productId,
      storeId,
      countedQuantity,
      reason,
    }: {
      productId: UUID;
      storeId: UUID;
      countedQuantity: number;
      reason: string;
    }) => inventoryService.setStockCount(productId, storeId, countedQuantity, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all });
      toast.success('Đã cập nhật tồn theo số đếm');
    },
  });
}

/** Tăng tồn khi biết rõ số lượng thêm vào (`INCREASE`). */
export function useIncreaseStock() {
  const inventoryService = useInventoryService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      productId,
      storeId,
      quantity,
      reason,
    }: {
      productId: UUID;
      storeId: UUID;
      quantity: number;
      reason: string;
    }) => inventoryService.increaseStock(productId, storeId, quantity, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all });
      toast.success('Đã tăng tồn kho');
    },
  });
}

/** Giảm tồn khi biết rõ số lượng mất (`DECREASE`). BE chặn giảm quá tồn. */
export function useDecreaseStock() {
  const inventoryService = useInventoryService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      productId,
      storeId,
      quantity,
      reason,
    }: {
      productId: UUID;
      storeId: UUID;
      quantity: number;
      reason: string;
    }) => inventoryService.decreaseStock(productId, storeId, quantity, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all });
      toast.success('Đã giảm tồn kho');
    },
  });
}

/**
 * Hook to record damaged stock
 */
export function useRecordDamage() {
  const inventoryService = useInventoryService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      productId,
      storeId,
      quantity,
      reason,
    }: {
      productId: UUID;
      storeId: UUID;
      quantity: number;
      reason: string;
    }) => inventoryService.recordDamage(productId, storeId, quantity, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all });
      toast.success('Đã ghi nhận hàng hư hỏng');
    },
  });
}

// ĐÃ GỠ 04/09/2026: `useRecordLoss`, `useTransferOut`, `useTransferIn`.
// Ba loại LOSS / TRANSFER_OUT / TRANSFER_IN không có trong ChoiceField của BE
// nên mọi lời gọi đều 400. Ghi nhận hàng mất giờ dùng `useDecreaseStock`.
// Chuyển kho thì CHƯA có đường nào — đừng ánh xạ tạm sang tăng/giảm, vì đó là
// hai bút toán ở hai cửa hàng và gộp lại sẽ ghi sai lịch sử.

// ============================================
// Lô & hạn sử dụng (sheet 2 #32)
// ============================================
//
// `retry: false` cho MỌI query mới ở dưới: các endpoint này chưa có trên QA nên
// hiện tại trả 404. Với retry mặc định (3 lần, backoff) màn hình treo ở loading
// gần chục giây rồi mới báo lỗi, người test tưởng trang hỏng. Tắt retry thì vào
// thẳng error state. Bỏ dòng này đi khi BE đã deploy.

/**
 * Danh sách lô.
 *
 * `include_empty` mặc định của BE là false (ẩn lô đã hết) — truyền tường minh
 * để trạng thái toggle trên màn hình luôn khớp với thứ server thực sự trả về.
 */
export function useInventoryBatches(params: BatchListParams = {}) {
  const inventoryService = useInventoryService();

  return useQuery({
    queryKey: queryKeys.inventory.batches(params as Record<string, unknown>),
    queryFn: () => inventoryService.listBatches(params),
    retry: false,
  });
}

/**
 * Lô cận hạn.
 *
 * Kết quả LUÔN gồm cả lô đã quá hạn, kể cả khi `days` nhỏ — đừng lọc lại theo
 * `days_to_expiry` ở phía gọi.
 */
export function useExpiringBatches(params: ExpiringBatchParams = {}) {
  const inventoryService = useInventoryService();

  return useQuery({
    queryKey: queryKeys.inventory.expiringBatches(params as Record<string, unknown>),
    queryFn: () => inventoryService.listExpiringBatches(params),
    retry: false,
  });
}

/**
 * Ghi lô mới.
 *
 * Chỉ invalidate `inventory.all` chứ không chỉ riêng nhánh lô: với
 * `apply_to_stock=true` thì lời gọi này CỘNG KHO thật, bảng tồn kho đang mở ở
 * tab khác phải thấy số mới.
 */
export function useCreateBatch() {
  const inventoryService = useInventoryService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateBatchRequest) => inventoryService.createBatch(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all });
    },
  });
}

// ============================================
// Kiểm kê định kỳ (sheet 2 #30)
// ============================================
//
// PHÂN VAI đã chốt, để không ai gộp hai đường này làm một:
//   - `SET_ABSOLUTE` (AdjustStockModal) — sửa lẻ MỘT mặt hàng ngoài phiên.
//   - Phiên kiểm kê — đếm định kỳ nhiều mặt hàng, có hồ sơ và có lúc chốt.
// Cả hai cuối cùng đều đổ về /inventory/adjustments/ nên lịch sử vẫn là một.

export function useStocktakes(params: StocktakeListParams = {}, options?: { enabled?: boolean }) {
  const inventoryService = useInventoryService();

  return useQuery({
    queryKey: queryKeys.inventory.stocktakes(params as Record<string, unknown>),
    queryFn: () => inventoryService.listStocktakes(params),
    enabled: options?.enabled !== false,
    retry: false,
  });
}

/** Chi tiết phiên (kèm `lines[]` + `totals`). */
export function useStocktake(id: UUID | undefined, options?: { enabled?: boolean }) {
  const inventoryService = useInventoryService();

  return useQuery({
    queryKey: queryKeys.inventory.stocktakeDetail(id ?? ''),
    queryFn: () => inventoryService.getStocktake(id!),
    enabled: options?.enabled !== false && !!id,
    retry: false,
  });
}

/** Mở phiên. Cửa hàng đã có phiên OPEN thì BE trả 400 (ràng buộc DB). */
export function useCreateStocktake() {
  const inventoryService = useInventoryService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { store_id: UUID; note?: string }) =>
      inventoryService.createStocktake(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all });
    },
  });
}

/**
 * Lưu số đếm một mặt hàng.
 *
 * Invalidate chi tiết phiên sau mỗi lần lưu là BẮT BUỘC, không phải tối ưu:
 * `expected_quantity` được server chụp lại tại thời điểm lưu, nên bản trong
 * cache đã cũ ngay khi request trả về.
 */
export function useSaveStocktakeLine() {
  const inventoryService = useInventoryService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      stocktakeId,
      productId,
      countedQuantity,
    }: {
      stocktakeId: UUID;
      productId: UUID;
      countedQuantity: number;
    }) =>
      inventoryService.saveStocktakeLine(stocktakeId, {
        product_id: productId,
        counted_quantity: countedQuantity,
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.inventory.stocktakeDetail(variables.stocktakeId),
      });
    },
  });
}

/**
 * Chốt phiên — sinh bút toán thật, không thể hoàn tác.
 *
 * `retry: 1` mặc định của queryClient KHÔNG áp ở đây (mutation này để mặc định
 * cũng chỉ gửi lại khi request hỏng ở tầng mạng, còn lần thứ hai sau khi server
 * đã ghi thì nhận 400 chứ không trừ kho lần nữa — hợp đồng idempotent của BE).
 */
export function useFinalizeStocktake() {
  const inventoryService = useInventoryService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: UUID) => inventoryService.finalizeStocktake(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all });
    },
  });
}

/** Huỷ phiên — không ghi bút toán nào, chỉ đóng băng phiên. */
export function useCancelStocktake() {
  const inventoryService = useInventoryService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: UUID) => inventoryService.cancelStocktake(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all });
    },
  });
}

// ============================================
// Helper Types
// ============================================

export interface InventoryStats {
  totalProducts: number;
  totalValue: number;
  lowStockCount: number;
  outOfStockCount: number;
}

/**
 * Hook to calculate inventory stats from inventory data
 * This aggregates data client-side since backend doesn't have a dedicated stats endpoint
 */
export function useInventoryStats(storeId: UUID | undefined, options?: { enabled?: boolean }) {
  const { data: inventoryData, isLoading: isLoadingInventory, error: inventoryError } = 
    useInventoryByStore(storeId, { page_size: 1000 }, options);
  
  const { data: lowStockData, isLoading: isLoadingLowStock, error: lowStockError } = 
    useLowStock(storeId, { page_size: 1 }, options);
  
  const { data: outOfStockData, isLoading: isLoadingOutOfStock, error: outOfStockError } = 
    useOutOfStock(storeId, { page_size: 1 }, options);

  const isLoading = isLoadingInventory || isLoadingLowStock || isLoadingOutOfStock;
  const error = inventoryError || lowStockError || outOfStockError;

  const stats: InventoryStats | undefined = inventoryData ? {
    totalProducts: inventoryData.count,
    totalValue: 0, // TODO: Calculate from product prices * quantities
    lowStockCount: lowStockData?.count ?? 0,
    outOfStockCount: outOfStockData?.count ?? 0,
  } : undefined;

  return {
    data: stats,
    isLoading,
    error,
  };
}
